// Date helpers for the tenant-improvements module.
//
// We centralize "is this date past?" here so the rule is defined once and
// both the project card and any future surfaces (project detail, milestones
// list) can reuse the same predicate. Keeping `new Date()` confined to this
// module also makes it easier to mock time in tests if we ever need to.

import isPast from 'date-fns/isPast'
import getDateForLocale from 'portal-modules/common/utils/get-date-for-locale'

/**
 * Returns true when `dateStr` parses to a moment strictly before "now".
 * Returns false for missing, empty, or unparseable inputs so callers do
 * not need to guard them.
 *
 * @param {string|null|undefined} dateStr - ISO date or datetime string.
 * @returns {boolean}
 */
export const isDatePast = (dateStr) => {
  if (!dateStr) return false
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return false
  return isPast(date)
}

/**
 * Locale-aware date formatter used wherever the module renders a raw ISO
 * string (project target dates, milestone due dates). Delegates to
 * `getDateForLocale` which routes pt-BR through DATE_ALPHA_LONG and
 * everything else through the default 'MMM D, YYYY' format. We guard
 * missing / unparseable inputs at this layer so callers do not have to.
 *
 * @param {string|null|undefined} dateStr - ISO date or datetime string.
 * @returns {string} Formatted date or empty string when input is invalid.
 */
export const formatDateForLocale = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''
  return getDateForLocale(dateStr)
}
